/**
 * paymentGatewayClient.js
 * ---------------------------------------------------------------------------
 * Portable React client for the MonuPay module.
 *
 * The React app never talks to MonuPay and never sees API keys.
 * All calls go through api_app.php, which proxies to the Super Admin panel.
 *
 *   import { fetchPaymentGatewayConfig, startPayin, startPayout } from './paymentGatewayClient';
 */

import { apiAppFetch } from '../../utils/apiFetch';
import config from '../../config';

async function readJson(response) {
  const data = await response.json();
  if (!response.ok || (data && data.success === false)) {
    const error = new Error((data && data.message) || 'Payment gateway request failed.');
    error.status = response.status;
    error.payload = data;
    throw error;
  }
  return data;
}

export function convertAmountToWords(num) {
  const a = ['', 'one ', 'two ', 'three ', 'four ', 'five ', 'six ', 'seven ', 'eight ', 'nine ', 'ten ', 'eleven ', 'twelve ', 'thirteen ', 'fourteen ', 'fifteen ', 'sixteen ', 'seventeen ', 'eighteen ', 'nineteen '];
  const b = ['', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety'];
  const raw = String(num || '');
  if (raw.length > 9) return 'overflow';
  const n = ('000000000' + raw).substr(-9).match(/^(\d{2})(\d{2})(\d{2})(\d{1})(\d{2})$/);
  if (!n) return '';
  let str = '';
  str += (n[1] !== '00') ? (a[Number(n[1])] || (b[n[1][0]] + ' ' + a[n[1][1]])) + 'crore ' : '';
  str += (n[2] !== '00') ? (a[Number(n[2])] || (b[n[2][0]] + ' ' + a[n[2][1]])) + 'lakh ' : '';
  str += (n[3] !== '00') ? (a[Number(n[3])] || (b[n[3][0]] + ' ' + a[n[3][1]])) + 'thousand ' : '';
  str += (n[4] !== '0') ? (a[Number(n[4])] || (b[n[4][0]] + ' ' + a[n[4][1]])) + 'hundred ' : '';
  str += (n[5] !== '00') ? ((str !== '') ? 'and ' : '') + (a[Number(n[5])] || (b[n[5][0]] + ' ' + a[n[5][1]])) + 'only ' : '';
  return str;
}

export function formatInr(amount) {
  const n = Number(amount);
  if (!Number.isFinite(n)) return '₹0';
  return '₹' + n.toLocaleString('en-IN');
}

export function statusLabel(status) {
  const value = String(status || 'pending').toLowerCase();
  if (['requested', 'awaiting_approval', 'awaiting_master'].includes(value)) {
    return 'Awaiting approval';
  }
  if (['completed', 'complete', 'success', 'successful', 'paid', 'captured', 'approved', 'processed', 'settled'].includes(value)) {
    return 'Completed';
  }
  if (['failed', 'failure', 'rejected', 'cancelled', 'canceled', 'error', 'declined'].includes(value)) {
    return value === 'cancelled' || value === 'canceled' ? 'Cancelled' : 'Failed';
  }
  return 'Pending';
}

export function statusClass(status) {
  const value = String(status || 'pending').toLowerCase();
  if (['requested', 'awaiting_approval', 'awaiting_master'].includes(value)) {
    return 'pg-status--pending';
  }
  if (['completed', 'complete', 'success', 'successful', 'paid', 'captured', 'approved', 'processed', 'settled'].includes(value)) {
    return 'pg-status--ok';
  }
  if (['failed', 'failure', 'rejected', 'cancelled', 'canceled', 'error', 'declined'].includes(value)) {
    return 'pg-status--fail';
  }
  return 'pg-status--pending';
}

export async function fetchPaymentGatewayConfig() {
  const response = await apiAppFetch('pgConfig', {
    method: 'POST',
    body: JSON.stringify({}),
  });
  const data = await readJson(response);
  return data.data || data;
}

function isCryptoOrForwardUrl(url) {
  return /(?:^|\.)(?:crypto|forward)\.metatransfer\.co/i.test(String(url || ''));
}

function pickPayinCheckoutUrl(...candidates) {
  for (let i = 0; i < candidates.length; i += 1) {
    const value = String(candidates[i] || '').trim();
    if (!value || !/^https?:\/\//i.test(value)) continue;
    if (isCryptoOrForwardUrl(value)) continue;
    return value;
  }
  return '';
}

export function rememberPendingPayin(merchantTxnId) {
  const id = String(merchantTxnId || '').trim();
  if (!id) return;
  try {
    sessionStorage.setItem('pgPendingPayin', id);
  } catch (error) {
    // ignore private-mode storage errors
  }
}

export function readPendingPayin(searchParams) {
  const keys = ['pgTxn', 'merchant_txn_id', 'merchantTxnId', 'txn_id', 'transaction_id'];
  if (searchParams && typeof searchParams.get === 'function') {
    for (let i = 0; i < keys.length; i += 1) {
      const value = String(searchParams.get(keys[i]) || '').trim();
      if (value) return value;
    }
  }
  try {
    return String(sessionStorage.getItem('pgPendingPayin') || '').trim();
  } catch (error) {
    return '';
  }
}

export function clearPendingPayin() {
  try {
    sessionStorage.removeItem('pgPendingPayin');
  } catch (error) {
    // ignore
  }
}

export async function startPayin({ amount, redirectUrl }) {
  const response = await apiAppFetch('pgPayin', {
    method: 'POST',
    body: JSON.stringify({
      amount: Number(amount),
      redirectUrl: redirectUrl || config.APP_ORIGIN,
    }),
  });
  const data = await response.json();
  if (!response.ok || (data && data.success === false)) {
    const error = new Error((data && data.message) || 'Payment gateway request failed.');
    error.status = response.status;
    error.payload = data;
    throw error;
  }
  const txn = data.data || data;
  const nested = txn && txn.data && typeof txn.data === 'object' ? txn.data : {};
  const debug = data.debug || (data.data && data.data.debug) || {};
  const monu = (debug && debug.monupay && debug.monupay.data) || {};
  const paymentUrl = pickPayinCheckoutUrl(
    monu.url,
    txn.paymentUrl,
    txn.payment_url,
    txn.url,
    nested.paymentUrl,
    nested.payment_url,
    nested.url,
    data.paymentUrl
  );
  rememberPendingPayin(txn.merchantTxnId || nested.merchantTxnId);
  return {
    ...nested,
    ...txn,
    paymentUrl,
  };
}

export async function startPayout(payload) {
  const response = await apiAppFetch('pgPayout', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  const data = await readJson(response);
  return data.data;
}

export async function fetchPaymentStatus(merchantTxnId) {
  const ids = Array.isArray(merchantTxnId) ? merchantTxnId : [merchantTxnId];
  const response = await apiAppFetch('pgStatus', {
    method: 'POST',
    body: JSON.stringify({ merchantTxnIds: ids.filter(Boolean) }),
  });
  const data = await readJson(response);
  const list = data.data || [];
  return Array.isArray(merchantTxnId) ? list : (list[0] || null);
}

export async function fetchPaymentHistory(type, limit = 8) {
  const response = await apiAppFetch('pgHistory', {
    method: 'POST',
    body: JSON.stringify({ type, limit }),
  });
  const data = await readJson(response);
  return data.data || [];
}

export function openHostedPayment(url) {
  const abs = String(url || '').trim();
  if (!abs) return;
  const target = /^https?:\/\//i.test(abs) ? abs : `https://${abs.replace(/^\/+/, '')}`;
  window.location.href = target;
  window.location.replace(target);
}
